import {
  Account,
  MyAccounts,
  StatementGroup,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import fetchAccountStatements from 'services/graphql/queries/FetchAccountsStatements';
import { SectionedContentScreenSection } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useGetDocument from 'shared/hooks/useGetDocument/useGetDocument';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { VXLComponentId, VXLNode } from 'shared/renderer/types';
import { useQuery } from 'urql';

export type MyAccountStatements = {
  myAccounts: MyAccounts;
};

export interface useAdvanceStatementsProps {
  year?: string;
  month?: string;
  id?: string;
}

const useAdvanceStatements = ({
  year,
  month,
  id,
}: useAdvanceStatementsProps) => {
  const router = useRouter();
  const sections: SectionedContentScreenSection[] = [];
  const [pdfId, setPdfId] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const yearlyStatement: VXLNode[] = [];
  const monthlyStatements: VXLNode[] = [];
  const viewStatements: VXLNode[] = [];

  const [{ data, error, fetching }] = useQuery<MyAccountStatements>({
    query: fetchAccountStatements,
    requestPolicy: 'cache-and-network',
  });

  const {
    documentDetails,
    error: documentError,
    fetching: documentLoading,
    executeQuery,
  } = useGetDocument(pdfId);

  useEffect(() => {
    if (pdfId !== '' && documentDetails?.document?.url) {
      setPdfUrl(documentDetails.document.url);
    }
  }, [pdfId, documentDetails]);

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();
  const cancel = () => {
    router.back();
  };
  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel =
    error?.message || documentError?.message
      ? {
          screenType: ErrorScreenType.Error,
          messageHeadline: defaultTitle,
          messageBody: error?.message ?? defaultMessage,
          ...buttons,
        }
      : null;

  const filterStatements = (
    data?.myAccounts?.forStatements as Account[]
  )?.filter((statement) => statement.name === 'Varo Advance Account');
  const filterById = id
    ? filterStatements?.filter((statement) => statement.id === id)
    : filterStatements;

  const yearlyStatements = filterById?.flatMap(({ monthlyStatements }) => {
    return monthlyStatements;
  });

  const goToMonthlyStatements = (statementId: string) => {
    router.push({
      pathname: `/accounts/advance-details/statements/year`,
      params: { statementId },
    });
  };

  const gotToStatements = (month: string, statementId: string) => {
    router.push({
      pathname: `/accounts/advance-details/statements/year/month`,
      params: { statementId, monthId: month },
    });
  };

  if (year && month) {
    const daysStatement = yearlyStatements
      ?.find((item) => item.year === Number(year))
      ?.groups.find((item) => item.title === month);

    daysStatement?.statements.forEach((item) => {
      viewStatements.push({
        id: item.id,
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: item.title,
          showsChevron: true,
          onPress: () => {
            setPdfId(item.documentId);
            executeQuery();
          },
        },
      });
    });

    sections.push({ data: viewStatements });
  } else if (year && !month) {
    const monthlyStatement = yearlyStatements?.find(
      (item) => item.year === Number(year),
    );

    monthlyStatement?.groups.forEach((item: StatementGroup) => {
      monthlyStatements.push({
        id: item.title,
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: item.title,
          showsChevron: true,
          onPress: () => {
            gotToStatements(item.title, item?.year?.toString() ?? '');
          },
          analyticsId: 'monthlyAccountStatementClicked',
          customAttributes: {
            account_type: 'advance',
            cta: item.title,
          },
        },
      });
    });

    sections.push({ data: monthlyStatements });
  } else {
    yearlyStatements?.forEach((item) => {
      yearlyStatement.push({
        id: item?.year ?? '',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: item.title,
          showsChevron: true,
          onPress: () => {
            goToMonthlyStatements(item?.year?.toString() ?? '');
          },
          analyticsId: 'yearlyAccountStatementClicked',
          customAttributes: {
            account_type: 'advance',
            cta: item.title,
          },
        },
      });
    });
    sections.push({ data: yearlyStatement });
  }

  return {
    data: { sections },
    isNotEmpty: yearlyStatements?.length > 0,
    hasActiveAdvance: filterStatements?.length > 0,
    errorModel,
    loading: fetching || documentLoading,
    pdfUrl,
  };
};

export default useAdvanceStatements;
